import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

import { Note } from '../..';
export const _frontmatter = {
  "title": "Components"
};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <h1 {...{
      "id": "components"
    }}>{`Components`}</h1>
    <p>{`A number of built-in UI components are available for layouts, grids, buttons, form elements, and more.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx",
        "metastring": "live=true",
        "live": "true"
      }}>{`<Box
  p={4}
  bg='highlight'>
  <Flex
    sx={{
      alignItems: 'center',
    }}>
    <Heading>
      Components
    </Heading>
    <Button ml='auto'>
      Beep
    </Button>
  </Flex>
</Box>
`}</code></pre>
    <h2 {...{
      "id": "variants"
    }}>{`Variants`}</h2>
    <p>{`Theme UI components can be customized by adding styles to your theme.
Each component accepts a `}<inlineCode parentName="p">{`variant`}</inlineCode>{` prop to quickly
change custom stylistic variations that you define.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`{
  // ...base theme...
  // After the base theme, define the variants:
  buttons: {
    secondary: {
      fontWeight: 'bold',
      color: 'white',
      bg: 'primary',
      '&:hover': {
        bg: 'dark',
      },
    },
  },
  text: {
    caps: {
      textTransform: 'uppercase',
      letterSpacing: '.2em',
    },
    heading: {
      fontFamily: 'heading',
      fontWeight: 'heading',
      lineHeight: 'heading',
    },
    display: {
      // extends the text.heading styles
      variant: 'text.heading',
      fontSize: [6, 7, 8],
      fontWeight: 'display',
    },
  },
  cards: {
    primary: {
      padding: 2,
      borderRadius: 4,
      boxShadow: '0 0 4px 1px rgba(0, 0, 0, 0.5)',
    },
  },
}
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-jsx",
        "metastring": "live=true",
        "live": "true"
      }}>{`<Button variant='secondary'>
  Secondary Button
</Button>
`}</code></pre>
    <h2 {...{
      "id": "props"
    }}>{`Props`}</h2>
    <p>{`All Theme UI components include the following props.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Name`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Type`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`sx`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Object`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "/sx-prop"
            }}>{`Theme-aware styles`}</a></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`variant`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`String`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Applies a theme `}<a parentName="td" {...{
              "href": "/guides/variants"
            }}>{`variant`}</a>{` style`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`as`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`String`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Changes the underlying HTML element`}</td>
        </tr>
      </tbody>
    </table>
    <Note mdxType="Note">
      <p>{`The `}<inlineCode parentName="p">{`sx`}</inlineCode>{` prop on Theme UI components can be used `}<em parentName="p">{`without`}</em>{` the `}<inlineCode parentName="p">{`jsx`}</inlineCode>{` pragma comment and can be used in MDX files to apply theme-based styles.`}</p>
    </Note>
    <h3 {...{
      "id": "style-props"
    }}>{`Style Props`}</h3>
    <p>{`Each Theme UI component includes shorthand props for applying margin, padding, and color styles.
These props work the same as properties within the `}<inlineCode parentName="p">{`sx`}</inlineCode>{` prop, but allow for a more terse syntax for readability.`}</p>
    <Note mdxType="Note">
      <p>{`If you've used `}<a parentName="p" {...{
          "href": "https://styled-system.com"
        }}>{`Styled System`}</a>{` or `}<a parentName="p" {...{
          "href": "https://rebassjs.org"
        }}>{`Rebass`}</a>{` before,
these props work the same way.`}</p>
    </Note>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Name`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`color`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Foreground color`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`bg`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Background color`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`margin`}</inlineCode>{`, `}<inlineCode parentName="td">{`m`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Margin`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`marginTop`}</inlineCode>{`, `}<inlineCode parentName="td">{`mt`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Margin Top`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`marginRight`}</inlineCode>{`, `}<inlineCode parentName="td">{`mr`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Margin Right`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`marginBottom`}</inlineCode>{`, `}<inlineCode parentName="td">{`mb`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Margin Bottom`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`marginLeft`}</inlineCode>{`, `}<inlineCode parentName="td">{`ml`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Margin Left`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`marginX`}</inlineCode>{`, `}<inlineCode parentName="td">{`mx`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Margin Left & Right`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`marginY`}</inlineCode>{`, `}<inlineCode parentName="td">{`my`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Margin Top & Bottom`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`padding`}</inlineCode>{`, `}<inlineCode parentName="td">{`p`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Padding`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`paddingTop`}</inlineCode>{`, `}<inlineCode parentName="td">{`pt`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Padding Top`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`paddingRight`}</inlineCode>{`, `}<inlineCode parentName="td">{`pr`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Padding Right`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`paddingBottom`}</inlineCode>{`, `}<inlineCode parentName="td">{`pb`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Padding Bottom`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`paddingLeft`}</inlineCode>{`, `}<inlineCode parentName="td">{`pl`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Padding Left`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`paddingX`}</inlineCode>{`, `}<inlineCode parentName="td">{`px`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Padding Left & Right`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`paddingY`}</inlineCode>{`, `}<inlineCode parentName="td">{`py`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Padding Top & Bottom`}</td>
        </tr>
      </tbody>
    </table>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      